<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Check, Info } from 'lucide-svelte';
  import { signalFilters } from '$lib/stores/selection';
  import { signalCatalogue, signalCatalogueFailed, loadSignalCatalogue } from '$lib/stores/signals';
  import type { SignalSelection } from '$lib/types/api';

  /**
   * Register events: a timespan, and any of the ticked events within it.
   *
   * One shape for every signal, where the capital filter it replaces asked for
   * a date range, a number of rounds, a percentage and a resulting capital —
   * and whose defaults halved the result without saying so. The only argument
   * left is the least amount raised, and only for a capital increase. Where a
   * company moved to is the region filter's to say.
   */

  /** The least amounts offered for a capital increase, CHF; 0 is any. */
  const minAmounts = [0, 50000, 100000, 500000, 1000000];

  let explaining: string | null = null;

  /** A timespan picked before any signal is ticked, which has nowhere to go yet. */
  let pickedDays: number | null = null;

  $: catalogue = $signalCatalogue;
  $: selected = $signalFilters?.selected ?? [];
  $: days = $signalFilters?.days ?? pickedDays ?? catalogue?.default_days ?? 90;

  /**
   * Switched on by the customer or by a selection arriving (a saved search,
   * the assistant), and off only by the customer, which clears it — the
   * contract every filter in this panel keeps.
   */
  let enabled = false;
  $: if (selected.length > 0 || $signalFilters?.capital_raised) enabled = true;

  function toggleEnabled() {
    enabled = !enabled;
    if (!enabled) signalFilters.set(undefined);
  }

  onMount(loadSignalCatalogue);

  function write(nextSelected: SignalSelection[], nextDays: number) {
    signalFilters.set(
      nextSelected.length > 0 ? { days: nextDays, selected: nextSelected } : undefined
    );
  }

  function isOn(slug: string, list: SignalSelection[]): boolean {
    return list.some((s) => s.type === slug);
  }

  function toggleSignal(slug: string) {
    write(
      isOn(slug, selected)
        ? selected.filter((s) => s.type !== slug)
        : [...selected, { type: slug }],
      days
    );
  }

  function setDays(next: number) {
    if (selected.length > 0) write(selected, next);
    else pickedDays = next;
  }

  function setMinAmount(slug: string, amount: number) {
    write(
      selected.map((s) =>
        s.type === slug ? { type: slug, ...(amount > 0 ? { min_amount: amount } : {}) } : s
      ),
      days
    );
  }

  function minAmountOf(slug: string, list: SignalSelection[]): number {
    return list.find((s) => s.type === slug)?.min_amount ?? 0;
  }

  const chf = (n: number) => `CHF ${n.toLocaleString('de-CH')}`;
</script>

<div class={enabled ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  <div class="mb-3">
    <label class="group flex cursor-pointer items-center">
      <input
        type="checkbox"
        checked={enabled}
        on:click={toggleEnabled}
        class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
      />
      <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
        />
      </svg>
      <span class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700">
        {$_('filters.signals.title')}
      </span>
    </label>
  </div>

  {#if !enabled}
    <!-- Ticked open, or nothing. -->
  {:else if $signalCatalogueFailed}
    <p class="text-xs text-gray-500">{$_('filters.signals.unavailable')}</p>
  {:else if !catalogue}
    <p class="text-xs text-gray-400">{$_('filters.signals.loading')}</p>
  {:else}
    <div class="space-y-3">
      <!-- The timespan, once for every signal. A select, as the side panel is
           too narrow for a row of chips. -->
      <label class="flex items-center gap-2 text-xs text-gray-500">
        {$_('filters.signals.timespan')}
        <select
          class="rounded-control border border-hairline bg-surface-panel px-2 py-1 text-xs"
          value={days}
          on:change={(e) => setDays(Number(e.currentTarget.value))}
        >
          {#each catalogue.days as d (d)}
            <option value={d}>{$_('filters.signals.days', { values: { n: d } })}</option>
          {/each}
        </select>
      </label>

      <div>
        <p class="mb-1 text-xs text-gray-400">{$_('filters.signals.anyOf')}</p>
        <div class="grid grid-cols-1 gap-1">
          {#each catalogue.signals as kind (kind.slug)}
            {@const on = isOn(kind.slug, selected)}
            <div>
              <div
                class="flex items-center rounded-control border transition-colors {on
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-hairline bg-surface-panel hover:border-accent-300 hover:bg-gray-50'}"
              >
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-2 text-left"
                  aria-label={kind.label}
                  aria-pressed={on}
                  on:click={() => toggleSignal(kind.slug)}
                >
                  <span
                    class="flex h-3 w-3 shrink-0 items-center justify-center rounded border {on
                      ? 'border-accent-500 bg-accent-500 text-white'
                      : 'border-gray-300'}"
                  >
                    {#if on}<Check class="h-3 w-3" />{/if}
                  </span>
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900" title={kind.label}>
                    {kind.label}
                  </span>
                </button>
                {#if kind.means}
                  <button
                    type="button"
                    class="shrink-0 rounded-control px-1.5 py-1.5 text-gray-400 transition hover:text-accent-600"
                    aria-label={kind.means}
                    on:mouseenter={() => (explaining = kind.slug)}
                    on:mouseleave={() => (explaining = null)}
                    on:focus={() => (explaining = kind.slug)}
                    on:blur={() => (explaining = null)}
                  >
                    <Info class="h-3.5 w-3.5" />
                  </button>
                {/if}
              </div>

              {#if explaining === kind.slug && kind.means}
                <p class="px-2 pb-0.5 pt-1 text-xs text-ink-500">{kind.means}</p>
              {/if}

              {#if on && kind.takes_min_amount}
                <!-- The one argument a signal takes, shown only once it is chosen. -->
                <label class="mt-1 flex items-center gap-2 px-2 text-xs text-gray-500">
                  {$_('filters.signals.minAmount')}
                  <select
                    class="rounded-control border border-hairline bg-surface-panel px-2 py-1 text-xs"
                    value={minAmountOf(kind.slug, selected)}
                    on:change={(e) => setMinAmount(kind.slug, Number(e.currentTarget.value))}
                  >
                    {#each minAmounts as amount (amount)}
                      <option value={amount}
                        >{amount === 0 ? $_('filters.signals.anyAmount') : chf(amount)}</option
                      >
                    {/each}
                  </select>
                </label>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}
</div>
